import { Dispatch } from 'redux';
import {
  COMPANY_MANAGER_FETCH_REQUEST,
  COMPANY_MANAGER_FETCH_SUCCESS,
  COMPANY_MANAGER_FETCH_FAILURE,
  COMPANY_MANAGER_SET_SEARCH,
  COMPANY_MANAGER_SET_SHOW_ARCHIVED,
  COMPANY_MANAGER_SET_FILTERS,
  COMPANY_MANAGER_CLEAR_FILTERS,
  COMPANY_MANAGER_SET_PAGE,
  COMPANY_MANAGER_SET_PAGE_SIZE,
  COMPANY_MANAGER_SET_INDUSTRY_OPTIONS,
  COMPANY_MANAGER_ADD_REQUEST,
  COMPANY_MANAGER_ADD_SUCCESS,
  COMPANY_MANAGER_ADD_FAILURE,
  COMPANY_MANAGER_FETCH_DROPDOWN_REQUEST,
  COMPANY_MANAGER_FETCH_DROPDOWN_SUCCESS,
  COMPANY_MANAGER_FETCH_DROPDOWN_FAILURE,
} from './actionTypes';
import { apiFetch } from '../services/api';
import { RootState } from '../types';

export interface CompanyManagerFiltersState {
  industry: string[];
  specialties: string[];
  employeeCountRange: [number, number];
  zone: string[];
  country: string[];
}

export const EMPTY_COMPANY_FILTERS: CompanyManagerFiltersState = {
  industry: [],
  specialties: [],
  employeeCountRange: [0, 0],
  zone: [],
  country: [],
};

interface FetchRequest { type: typeof COMPANY_MANAGER_FETCH_REQUEST }
interface FetchSuccess {
  type: typeof COMPANY_MANAGER_FETCH_SUCCESS;
  payload: {
    data: any[];
    pagination: { total: number; page: number; pageSize: number; totalPages: number };
  };
}
interface FetchFailure { type: typeof COMPANY_MANAGER_FETCH_FAILURE; payload: string }
interface SetSearch { type: typeof COMPANY_MANAGER_SET_SEARCH; payload: string }
interface SetShowArchived { type: typeof COMPANY_MANAGER_SET_SHOW_ARCHIVED; payload: boolean }
interface SetFilters { type: typeof COMPANY_MANAGER_SET_FILTERS; payload: CompanyManagerFiltersState }
interface ClearFilters { type: typeof COMPANY_MANAGER_CLEAR_FILTERS }
interface SetPage { type: typeof COMPANY_MANAGER_SET_PAGE; payload: number }
interface SetPageSize { type: typeof COMPANY_MANAGER_SET_PAGE_SIZE; payload: number }
interface SetIndustryOptions { type: typeof COMPANY_MANAGER_SET_INDUSTRY_OPTIONS; payload: { id: string; label: string }[] }
interface AddRequest { type: typeof COMPANY_MANAGER_ADD_REQUEST }
interface AddSuccess { type: typeof COMPANY_MANAGER_ADD_SUCCESS }
interface AddFailure { type: typeof COMPANY_MANAGER_ADD_FAILURE; payload: string }
interface FetchDropdownRequest { type: typeof COMPANY_MANAGER_FETCH_DROPDOWN_REQUEST }
interface FetchDropdownSuccess { type: typeof COMPANY_MANAGER_FETCH_DROPDOWN_SUCCESS; payload: any[] }
interface FetchDropdownFailure { type: typeof COMPANY_MANAGER_FETCH_DROPDOWN_FAILURE; payload: string }

export type CompanyManagerAction =
  | FetchRequest
  | FetchSuccess
  | FetchFailure
  | SetSearch
  | SetShowArchived
  | SetFilters
  | ClearFilters
  | SetPage
  | SetPageSize
  | SetIndustryOptions
  | AddRequest
  | AddSuccess
  | AddFailure
  | FetchDropdownRequest
  | FetchDropdownSuccess
  | FetchDropdownFailure;

export function companyManagerSetSearch(value: string): SetSearch {
  return { type: COMPANY_MANAGER_SET_SEARCH, payload: value };
}

export function companyManagerSetShowArchived(value: boolean): SetShowArchived {
  return { type: COMPANY_MANAGER_SET_SHOW_ARCHIVED, payload: value };
}

export function companyManagerSetFilters(filters: CompanyManagerFiltersState): SetFilters {
  return { type: COMPANY_MANAGER_SET_FILTERS, payload: filters };
}

export function companyManagerClearFilters(): ClearFilters {
  return { type: COMPANY_MANAGER_CLEAR_FILTERS };
}

export function companyManagerSetPage(page: number): SetPage {
  return { type: COMPANY_MANAGER_SET_PAGE, payload: page };
}

export function companyManagerSetPageSize(pageSize: number): SetPageSize {
  return { type: COMPANY_MANAGER_SET_PAGE_SIZE, payload: pageSize };
}

export function fetchCompanyIndustries() {
  return async (dispatch: Dispatch<CompanyManagerAction>, getState: () => RootState) => {
    try {
      const environment = getState().environment.current;
      const res = await apiFetch('/api/platform/companies/industries', { environment });
      const raw = res?.data ?? res;
      const list = (Array.isArray(raw) ? raw : []).map((i: any) => ({
        id: String(i.id),
        label: i.name ?? i.label ?? String(i.id),
      }));
      list.sort((a: any, b: any) => a.label.localeCompare(b.label));
      dispatch({ type: COMPANY_MANAGER_SET_INDUSTRY_OPTIONS, payload: list });
    } catch {
      dispatch({ type: COMPANY_MANAGER_SET_INDUSTRY_OPTIONS, payload: [] });
    }
  };
}

/** Fetches all companies without pagination for dropdowns (e.g. New Opportunity). */
export function fetchAllCompaniesForDropdown() {
  return async (dispatch: Dispatch<CompanyManagerAction>, getState: () => RootState) => {
    dispatch({ type: COMPANY_MANAGER_FETCH_DROPDOWN_REQUEST });
    try {
      const state = getState() as any;
      const environment = state.environment.current;
      // No page/pageSize so backend returns full list
      const url = '/api/platform/companies?showArchived=false';
      const res = await apiFetch(url, { environment });
      const companies = res.success && Array.isArray(res.data) ? res.data : [];
      dispatch({ type: COMPANY_MANAGER_FETCH_DROPDOWN_SUCCESS, payload: companies });
    } catch (error: any) {
      dispatch({
        type: COMPANY_MANAGER_FETCH_DROPDOWN_FAILURE,
        payload: error.message || 'Failed to fetch companies for dropdown',
      });
    }
  };
}

export function fetchAllCompanies() {
  return async (dispatch: Dispatch<CompanyManagerAction>, getState: () => RootState) => {
    dispatch({ type: COMPANY_MANAGER_FETCH_REQUEST });

    try {
      const state = getState() as any;
      const environment = state.environment.current;
      const { search, showArchived, filters, pagination } = state.companyManager;

      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (showArchived) params.set('showArchived', 'true');
      params.set('page', String(pagination.page));
      params.set('pageSize', String(pagination.pageSize));
      if (filters.industry?.length) filters.industry.forEach((id: string) => params.append('industry', id));
      if (filters.specialties?.length) filters.specialties.forEach((s: string) => params.append('specialties', s));
      if (filters.zone?.length) filters.zone.forEach((z: string) => params.append('zone', z));
      if (filters.country?.length) filters.country.forEach((c: string) => params.append('country', c));
      const [min, max] = filters.employeeCountRange ?? [0, 0];
      if (min > 0 || max > 0) {
        if (min > 0) params.set('employeeCountMin', String(min));
        if (max > 0) params.set('employeeCountMax', String(max));
      }

      const url = `/api/platform/companies?${params.toString()}`;
      const res = await apiFetch(url, { environment });

      const companies = res.success && Array.isArray(res.data) ? res.data : [];
      const pag = res.pagination ?? { total: companies.length, page: 1, pageSize: pagination.pageSize, totalPages: 1 };

      dispatch({ type: COMPANY_MANAGER_FETCH_SUCCESS, payload: { data: companies, pagination: pag } });
    } catch (error: any) {
      dispatch({
        type: COMPANY_MANAGER_FETCH_FAILURE,
        payload: error.message || 'Failed to fetch companies',
      });
    }
  };
}

export interface AddCompanyPayload {
  sync_linkedin: boolean;
  linkedin: string;
  name: string;
  description: string;
  picture: string;
  headquarters: string;
  website: string;
  specialties: string[];
  industry: string;
  country: string;
  employee_count: number;
  archived: boolean;
}

export function addCompany(data: AddCompanyPayload) {
  return async (dispatch: Dispatch<any>, getState: () => RootState) => {
    dispatch({ type: COMPANY_MANAGER_ADD_REQUEST });

    try {
      const environment = getState().environment.current;

      await apiFetch('/api/platform/companies', {
        environment,
        method: 'POST',
        body: data,
      });

      dispatch({ type: COMPANY_MANAGER_ADD_SUCCESS });

      // Refetch with clean state so the full list loads (no search/filters from Company Manager)
      dispatch(companyManagerClearFilters());
      dispatch(companyManagerSetSearch(''));
      dispatch(companyManagerSetPage(1));
      await dispatch(fetchAllCompanies() as any);
      await dispatch(fetchAllCompaniesForDropdown() as any);
    } catch (error: any) {
      dispatch({
        type: COMPANY_MANAGER_ADD_FAILURE,
        payload: error.message || 'Failed to add company',
      });
      throw error;
    }
  };
}

/** Set company archived state (true = archive, false = unarchive). Refetches lists after. */
export function setCompanyArchived(companyId: string, archives: boolean) {
  return async (dispatch: Dispatch<any>, getState: () => RootState) => {
    const environment = getState().environment.current;
    await apiFetch(`/api/platform/companies/${companyId}`, {
      environment,
      method: 'PATCH',
      body: { archives },
    });
    await dispatch(fetchAllCompanies() as any);
    await dispatch(fetchAllCompaniesForDropdown() as any);
  };
}
